import {useRef, useEffect, useState, useCallback} from 'react';

// https://usehooks.com/usePrevious/
export const usePrevious = value => {
	const ref = useRef(undefined);

	// update to the new value after render. optimized to run only when the value changes.
	useEffect(() => {
		ref.current = value;
	}, [value]);

	// return the currently stored (i.e. previous) value to the current renderer.
	return ref.current;
};

// array version of usePrevious
export const usePreviousMany = values => {
	const ref = useRef([]);

	/* eslint-disable react-hooks/exhaustive-deps */
	useEffect(() => {
		ref.current = values;
	}, values);
	/* eslint-enable react-hooks/exhaustive-deps */

	return ref.current;
};

// https://stackoverflow.com/questions/58979309/checking-if-a-component-is-unmounted-using-react-hooks
export const useIsMounted = () => {
	const isMountedRef = useRef(true);
	const isMounted = useCallback(() => isMountedRef.current, []);

	useEffect(() => {
		return () => {
			isMountedRef.current = false;
		};
	}, []);

	return isMounted;
};

// https://github.com/facebook/react/issues/13029
export const useCombinedRefs = (...refs) => {
	return target => {
		refs.forEach(ref => {
			if (!ref) return;

			if (typeof ref === 'function') {
				ref(target);
			} else {
				ref.current = target;
			}
		});
	};
};

// https://usehooks.com/useMedia/
export const useMedia = query => {
	const [matches, setMatches] = useState(false);

	useEffect(() => {
		const mqList = window.matchMedia(query);

		setMatches(mqList.matches);

		const listener = () => setMatches(mqList.matches);
		mqList.addListener(listener);

		// cleanup
		return () => mqList.removeListener(listener);
	}, [query]);

	return matches;
};

// https://usehooks.com/useEventListener/
export const useEventListener = (eventName, handler, element = window) => {
	// Create a ref that stores handler
	const savedHandler = useRef();

	// Update ref.current value if handler changes.
	// This allows our effect below to always get latest handler ...
	// ... without us needing to pass it in effect deps array ...
	// ... and potentially cause effect to re-run every render.
	useEffect(() => {
		savedHandler.current = handler;
	}, [handler]);

	useEffect(
		() => {
			// Make sure element supports addEventListener
			// On
			const isSupported = element && element.addEventListener;
			if (!isSupported) return;

			// Create event listener that calls handler function stored in ref
			const eventListener = event => savedHandler.current(event);

			// Add event listener
			element.addEventListener(eventName, eventListener);

			// Remove event listener on cleanup
			return () => {
				element.removeEventListener(eventName, eventListener);
			};
		},
		[eventName, element], // Re-run if eventName or element changes
	);
};
